import { useCallback, useEffect, useRef, useState } from 'react'
import type { View } from 'react-native'
import { useLayoutObserver } from '../use-layout-observer'
import type {
  LayoutInfo,
  PositionConfig,
  UsePositioningLayoutProps,
} from './types'

const DEFAULT_CONFIG: Required<PositionConfig> = {
  recalculationDelay: 0,
  tolerance: 1,
}

interface UsePositioningLayoutReturn {
  layout: LayoutInfo | null
  isLayoutReady: boolean
  elementRef: React.RefObject<View>
  recalculateLayout: () => void
  handleLayoutChange: (event: {
    nativeEvent: {
      layout: { x: number; y: number; width: number; height: number }
    }
  }) => void
  hasLayoutChanged: (newLayout: { width: number; height: number }) => boolean
}

export function usePositioningLayout({
  isActive,
  config = {},
}: UsePositioningLayoutProps): UsePositioningLayoutReturn {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config }
  const [layout, setLayout] = useState<LayoutInfo | null>(null)
  const [isLayoutReady, setIsLayoutReady] = useState(false)
  const elementRef = useRef<View>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useLayoutObserver(elementRef)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const recalculateLayout = useCallback(() => {
    if (!elementRef.current) return

    elementRef.current.measureInWindow(
      (x: number, y: number, width: number, height: number) => {
        setLayout({ x, y, width, height })
        setIsLayoutReady(true)
      }
    )
  }, [])

  const resetLayout = useCallback(() => {
    setIsLayoutReady(false)
    setLayout(null)
  }, [])

  const scheduleLayoutRecalculation = useCallback(
    (delay = mergedConfig.recalculationDelay) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      setIsLayoutReady(false)

      if (delay > 0) {
        timeoutRef.current = setTimeout(recalculateLayout, delay)
      } else {
        recalculateLayout()
      }
    },
    [recalculateLayout, mergedConfig.recalculationDelay]
  )

  const hasLayoutChanged = useCallback(
    (newLayout: { width: number; height: number }): boolean => {
      if (!layout) return true

      return (
        Math.abs(layout.width - newLayout.width) > mergedConfig.tolerance ||
        Math.abs(layout.height - newLayout.height) > mergedConfig.tolerance
      )
    },
    [layout, mergedConfig.tolerance]
  )

  useEffect(() => {
    if (isActive) {
      scheduleLayoutRecalculation()
    } else {
      resetLayout()
    }
  }, [isActive, scheduleLayoutRecalculation, resetLayout])

  const handleLayoutChange = useCallback(
    (event: {
      nativeEvent: {
        layout: { x: number; y: number; width: number; height: number }
      }
    }) => {
      scheduleLayoutRecalculation(0)
    },
    [scheduleLayoutRecalculation]
  )

  return {
    layout,
    isLayoutReady,
    elementRef,
    recalculateLayout,
    handleLayoutChange,
    hasLayoutChanged,
  }
}
